import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import axios, {
	AxiosError,
	AxiosInstance,
	AxiosResponse,
	InternalAxiosRequestConfig,
} from "axios";
import { Endpoint } from "@/repository/endpoint";
import { RefreshTokenError, TokenManager } from "@/common/authentication";
import { isTokenExpired } from "@/common/authentication/jwtDecoder";
import { captureError } from "@/common/helpers/sentryHelper";
import { Controller } from "../controller";

export class RefreshTokenInterceptor {
	private isRefreshing = false;
	private failedQueue: Array<any> = [];
	private tokenManager: TokenManager = new TokenManager();

	private tokenRefreshRequiredStatusCodes = [401, 403];

	public setupTo = (axiosInstance: AxiosInstance): AxiosInstance => {
		axiosInstance.interceptors.request.use(
			(c: InternalAxiosRequestConfig) => this.onRequest(c),
			(e: AxiosError) => this.onRequestError(e)
		);
		axiosInstance.interceptors.response.use(
			(e: AxiosResponse) => this.onResponse(e),
			(e: AxiosError) => this.onResponseError(e)
		);

		return axiosInstance;
	};

	private processQueue(error: any, token: string | null = null) {
		this.failedQueue.forEach((prom) => {
			if (error) {
				prom.reject(error);
			} else {
				prom.resolve(token);
			}
		});

		this.failedQueue = [];
	}

	private async refreshToken(
		originalRequest: InternalAxiosRequestConfig
	): Promise<InternalAxiosRequestConfig> {
		try {
			const token = await this.tokenManager.tryRefreshToken();
			originalRequest.headers["Authorization"] = `Bearer ${token}`;
			this.processQueue(null, token);
			this.isRefreshing = false;

			return originalRequest;
		} catch (error: any) {
			const refreshTokenError = error as RefreshTokenError;

			if (!!refreshTokenError.data) {
				this.processQueue(refreshTokenError.data, null);
			}

			this.isRefreshing = false;
			throw refreshTokenError;
		}
	}

	private queueRequestUntilTokenReady(
		config: InternalAxiosRequestConfig
	): Promise<InternalAxiosRequestConfig> {
		return new Promise((resolve, reject) => {
			this.failedQueue.push({ resolve, reject });
		})
			.then((newToken) => {
				config.headers["Authorization"] = `Bearer ${newToken}`;
				return Promise.resolve(config);
			})
			.catch((err) => {
				return Promise.reject(err);
			});
	}

	private async onRequest(
		config: InternalAxiosRequestConfig
	): Promise<InternalAxiosRequestConfig> {
		const token: string = Lockr.get(StorageKeys.TokenId);
		config.headers["__tenant"] = Lockr.get(StorageKeys.TenantId) || "";
		config.withCredentials = true;

		// If no token exists, try to get one before proceeding
		if (!token) {
			if (this.shouldSkipRefreshToken(config.url || "")) {
				return config;
			}

			if (this.isRefreshing) {
				return this.queueRequestUntilTokenReady(config);
			}

			this.isRefreshing = true;

			try {
				return await this.refreshToken(config);
			} catch (error: any) {
				captureError(
					error,
					"RefreshTokenInterceptor.onRequest no token error"
				);

				return Promise.reject(error);
			}
		}

		if (
			!isTokenExpired(token, 20) ||
			this.shouldSkipRefreshToken(config.url || "")
		) {
			config.headers["Authorization"] = `Bearer ${token}`;

			return Promise.resolve(config);
		}

		if (this.isRefreshing) {
			return this.queueRequestUntilTokenReady(config);
		}

		this.isRefreshing = true;

		try {
			return await this.refreshToken(config);
		} catch (error: any) {
			captureError(error, "RefreshTokenInterceptor.onRequest token expired error");

			return Promise.reject(error);
		}
	}

	private onRequestError(error: AxiosError): Promise<AxiosError> {
		return Promise.reject(error);
	}

	private onResponse(response: AxiosResponse): AxiosResponse {
		return response.data;
	}

	private async onResponseError(error: AxiosError) {
		const responseStatus = error?.response?.status;
		if (
			responseStatus &&
			!this.tokenRefreshRequiredStatusCodes.includes(responseStatus)
		) {
			return Promise.reject(error);
		}

		if (!this.canBeTokenRefreshed(error) || !error?.config) {
			// Clear the queue and fail all pending requests if refresh is not possible
			if (this.isRefreshing) {
				this.processQueue(error, null);
				this.isRefreshing = false;
			}
			return Promise.reject(error);
		}

		try {
			await this.refreshToken(error.config);

			return axios.request(error.config);
		} catch (_error) {
			return Promise.reject(_error);
		}
	}

	private canBeTokenRefreshed(error: AxiosError): boolean {
		// Token cannot be refreshed if no error information is available
		if (!error || !error.response) {
			return false;
		}

		// Don't attempt refresh if server explicitly says token validation failed
		if (
			error.response.status === 401 &&
			(error.response.data === "Token validation failed." ||
				error.response.data === "Bearer token missing.")
		) {
			return false;
		}

		return true;
	}

	private shouldSkipRefreshToken(url: string): boolean {
		return [
			Endpoint.TenantId,
			Endpoint.Client,
			Endpoint.SignInAzureAD,
			Endpoint.SignOut,
			Controller.Deployments
		].some((x) => url.endsWith(x));
	}
}
